import { computed, effect, inject, Injectable, signal } from '@angular/core';
import { Route, Routes } from '@angular/router';
import { routes as ROUTES } from '../../app.routes';
import { Region, RegionallyRestrictedPage } from '../../models/region.model';
import { RmaService } from '../rma/rma.service';
import { RegionService } from '../region/region.service';
import { UserPermissionsService } from '../user-permissions/user-permissions.service';

@Injectable({
  providedIn: 'root',
})
export class NavigationMenuService {
  private regions = inject(RegionService);
  private permissions = inject(UserPermissionsService);
  private rmaService = inject(RmaService);
  private pendingApprovalCount = signal(0);
  private pendingApprovalRequestId = 0;

  constructor() {
    effect(() => {
      void this.refreshPendingApprovalCount();
    });
  }

  async refreshPendingApprovalCount() {
    const regionId = this.regions.selectedRegionId();
    if (!regionId) {
      this.pendingApprovalRequestId++;
      this.pendingApprovalCount.set(0);
      return;
    }

    await this.loadPendingApprovalCount(regionId);
  }

  // Used by the primary navigation menu,
  // filtered by selected region and user permissions.
  navigableItems = computed(() => {
    const selectedRegion = this.regions.selectedRegion();
    const permissionSet = this.permissions.permissionSet();
    const filter = this.routePermissionFilter(
      permissionSet.features,
      permissionSet.permissions,
      selectedRegion,
    );
    const clonedRoutes = this.cloneRoutes();
    this.applyPendingApprovalBadge(clonedRoutes);
    return this.filterRoutes(clonedRoutes, filter);
  });

  private cloneRoutes(): Routes {
    return JSON.parse(JSON.stringify(ROUTES));
  }

  private applyPendingApprovalBadge(routes: Routes) {
    for (const route of routes) {
      if (route.path === 'rma-management' && route.children) {
        const approvalsRoute = route.children.find(
          (child) => child.path === 'my-approvals',
        );

        const pendingRoute = approvalsRoute?.children?.find(
          (child) => child.path === 'pending',
        );

        if (pendingRoute?.data) {
          pendingRoute.data['badgeCount'] = this.pendingApprovalCount();
        }
      }

      if (route.children?.length) {
        this.applyPendingApprovalBadge(route.children);
      }
    }
  }

  private routePermissionFilter =
    (
      userFeatures: string[],
      userPermissions: string[],
      selectedRegion?: Region | null,
    ) =>
    (route: Route) => {
      // 1. Is the route explicitly hidden from navigation?
      const isHidden = route.data && route.data['hideFromNavigation'];
      if (isHidden) return false;

      // 2. Is the route a redirect?
      const isRedirect = !!route.redirectTo;
      // Redirects are not visible.
      if (isRedirect) return false;

      // 3. Are there any regional page restrictions?
      const pageKey = route.data?.['regionalPageRestrictionKey'] as
        | RegionallyRestrictedPage
        | undefined;
      const hasRegionAccess =
        pageKey && selectedRegion?.pageRestrictions[pageKey];
      // Important: Region restrictions supersede permissions.
      // Some features do not function for all regions.
      if (pageKey && !hasRegionAccess) return false;

      // 4. Admins (wildcard permission) can access everything.
      if (userPermissions?.includes('*:*')) return true;

      // 5. Does the route require a specific permission (e.g. "product:manage")?
      const requiredPermission = route.data?.['requiredPermission'] as
        | string
        | undefined;
      if (requiredPermission) {
        return userPermissions?.includes(requiredPermission) || false;
      }

      // 6. Does the route have an assigned feature-set?
      const feature: string | undefined = route.data?.['featureSet'];
      // Any public route is permissible and all routes are permissible for Admins.
      if (feature === 'public' || userFeatures?.includes('*')) return true;
      // If the route has a feature set, check if the user has that feature set.
      return feature ? userFeatures?.includes(feature) || false : false;
    };

  // Traverses the route tree recursively and runs the condition on each route.
  private filterRoutes(
    routes: Route[],
    condition: (route: Route) => boolean,
  ): Route[] {
    return routes.filter((route) => {
      const isVisible = condition(route);

      if (route.path === 'rma-management' && !isVisible) {
        return false;
      }

      if (route.children && route.children.length > 0) {
        route.children = this.filterRoutes(route.children, condition);
        // If the route no longer has any children
        // AND has no component of its own, it's not visible.'
        if (route.children.length === 0 && !route.component) {
          return false;
        }
      }

      const hasVisibleChildren = route.children && route.children.length > 0;

      return isVisible || hasVisibleChildren;
    });
  }

  private async loadPendingApprovalCount(regionId: number) {
    const requestId = ++this.pendingApprovalRequestId;

    try {
      const pendingApprovalCount = await this.rmaService.getPendingApprovalCountForMyRmas(regionId);

      if (requestId !== this.pendingApprovalRequestId) {
        return;
      }

      this.pendingApprovalCount.set(pendingApprovalCount);
    } catch {
      if (requestId === this.pendingApprovalRequestId) {
        this.pendingApprovalCount.set(0);
      }
    }
  }
}
